import { useCallback, useEffect, useMemo, useState } from 'react';
import type { StarGridNavGroupPreference } from 'src/components/star-grid/navigation.types';
import {
  deleteReportNavDefault,
  deleteReportNavPreference,
  getReportNavPreference,
  putReportNavDefault,
  putReportNavPreference
} from 'src/components/star-grid/services/starGridService';
import {
  EMPTY_NAV_GROUP_PREFERENCE,
  PUBLISHED_NAV_GROUP_ORDER,
  hasNavGroupOverrides,
  normalizeNavGroupPreference,
  withPublishedOrder,
  withoutRedundantOrder
} from 'src/components/star-grid/utils/starGridNavGroups';

/** Which arrangement edits land in: this user's own, or (admins only) the default for everyone. */
export type NavPreferenceScope = 'mine' | 'default';

export interface UseStarGridNavPreferenceResult {
  /** What the nav renders from: the arrangement in `scope`, sitting on the published section order. */
  readonly preference: StarGridNavGroupPreference;
  /** Whether the arrangement in `scope` is set — what "reset" has to undo. */
  readonly hasOwnArrangement: boolean;
  readonly canEditDefault: boolean;
  readonly scope: NavPreferenceScope;
  readonly setScope: (scope: NavPreferenceScope) => void;
  /** Why the last save did not reach the service, if it did not. The local arrangement still stands. */
  readonly saveError: string | undefined;
  readonly update: (change: (current: StarGridNavGroupPreference) => StarGridNavGroupPreference) => void;
  readonly reset: () => void;
}

const message = (caught: unknown): string => (caught instanceof Error ? caught.message : 'Unknown error');

/**
 * One user's arrangement of the Reports nav, persisted by the StarGrid service.
 *
 * Server-side rather than in local storage because the arrangement is the user's, not the browser's:
 * they see the same nav on a second machine, and an arrangement is not lost when site data is cleared.
 *
 * Each change is written immediately rather than debounced. A change is one deliberate act — moving a
 * report, reordering a section, collapsing a group — so the writes are a handful per session, and a
 * timer would trade that for the risk of losing the last one on navigation. The local state updates
 * first, so the nav never waits on the round trip.
 */
export const useStarGridNavPreference = (): UseStarGridNavPreferenceResult => {
  const [stored, setStored] = useState<StarGridNavGroupPreference>(EMPTY_NAV_GROUP_PREFERENCE);
  const [adminDefault, setAdminDefault] = useState<StarGridNavGroupPreference>(EMPTY_NAV_GROUP_PREFERENCE);
  const [publishedOrder, setPublishedOrder] = useState<readonly string[]>(PUBLISHED_NAV_GROUP_ORDER);
  const [savedOnServer, setSavedOnServer] = useState(false);
  const [canEditDefault, setCanEditDefault] = useState(false);
  const [scope, setScope] = useState<NavPreferenceScope>('mine');
  const [saveError, setSaveError] = useState<string | undefined>(undefined);

  useEffect(() => {
    let current = true;
    const load = async (): Promise<void> => {
      try {
        const response = await getReportNavPreference();
        if (!current) return;
        // A missing or empty published order would erase the grouping, so the mirrored default stands
        // instead — and checking it here keeps a malformed response from also discarding the user's
        // own arrangement below.
        const published = response.default_group_order;
        if (Array.isArray(published) && published.length > 0) setPublishedOrder(published);
        setStored(normalizeNavGroupPreference(response.preference));
        setAdminDefault(normalizeNavGroupPreference(response.default_preference));
        setSavedOnServer(response.source === 'user');
        setCanEditDefault(response.can_edit_default === true);
      } catch {
        // The nav still lays out from the published default. Only this user's own arrangement is
        // missing, and an error banner over a working navigation would be the worse outcome.
      }
    };
    void load();
    return () => {
      current = false;
    };
  }, []);

  // Either half is enough: the service is holding an arrangement, or this session has made one.
  const ownSet = savedOnServer || hasNavGroupOverrides(stored);
  const editingDefault = scope === 'default' && canEditDefault;
  // A user with no arrangement of their own sees the admin default; their first edit copies it, so
  // the override replaces the default whole rather than merging with it.
  const base = editingDefault || !ownSet ? adminDefault : stored;
  const preference = useMemo(() => withPublishedOrder(base, publishedOrder), [base, publishedOrder]);

  const update = useCallback(
    (change: (current: StarGridNavGroupPreference) => StarGridNavGroupPreference) => {
      const next = withoutRedundantOrder(change(preference), publishedOrder);
      setSaveError(undefined);
      // Deliberately not reverted on failure: discarding the arrangement the user just made because
      // a write blipped loses their work to report a problem they can retry past.
      const fail = (caught: unknown) => setSaveError(message(caught));
      if (editingDefault) {
        setAdminDefault(next);
        void putReportNavDefault(next).catch(fail);
        return;
      }
      setStored(next);
      setSavedOnServer(true);
      void putReportNavPreference(next).catch(fail);
    },
    [preference, publishedOrder, editingDefault]
  );

  const reset = useCallback(() => {
    setSaveError(undefined);
    const fail = (caught: unknown) => setSaveError(message(caught));
    if (editingDefault) {
      setAdminDefault(EMPTY_NAV_GROUP_PREFERENCE);
      void deleteReportNavDefault().catch(fail);
      return;
    }
    setStored(EMPTY_NAV_GROUP_PREFERENCE);
    setSavedOnServer(false);
    void deleteReportNavPreference().catch(fail);
  }, [editingDefault]);

  return {
    preference,
    hasOwnArrangement: editingDefault ? hasNavGroupOverrides(adminDefault) : ownSet,
    canEditDefault,
    scope: editingDefault ? 'default' : 'mine',
    setScope,
    saveError,
    update,
    reset
  };
};
